import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Tractor, Location, Driver } from "@/types/tms";
import { findLocationByAddress } from "@/lib/location-utils";
import {
  mapLegToCreateTrip,
  assertTripBodyHasLatLng,
  diffCreateResponseForCorrection,
} from "@/lib/eapi-trip-mapper";
import type { EapiCreateTripBody } from "@/lib/eapi-trip-mapper";
import { isNoEapiState } from "@/lib/eapi-eligibility";
import {
  buildCustomFieldsForOrder,
  type CustomFieldEntry,
} from "@/lib/eapi-custom-fields";
import {
  listActiveLegTripsForOrder,
  supersedeLegTrip,
  insertActiveLegTrip,
} from "@/lib/leg-trip-sync";
import { isAlreadyCancelledUpstreamError } from "@/lib/eapi-cancel-utils";
import { legMirrorsParentEquipment } from "@/lib/confirm-plan-legs";
import { withTimeout } from "@/lib/confirm-execute";

import { formLocalToUtc } from "@/lib/timezone";

/* ─────────────────────────────────────────────────────────────────────────
 * Single source of truth for per-leg EAPI trip synchronization.
 *
 * Replaces the two former code paths:
 *   1. `pivotPerLegTrips` (module-level, DB-sourced, best-effort)
 *   2. The inline loop in the multi-leg local-write block (form-sourced,
 *      sync-then-persist, includes repair)
 *
 * Sole owner of multi-leg trip replacement (AGENTS.md). A failed withdrawal
 * throws, so nothing is created over a possibly-live trip.
 *
 * Caller flags:
 *   • `enableRepair`     — also recreate a missing trip for an active leg
 * ───────────────────────────────────────────────────────────────────────── */

export async function correctStaleCreateResponse(
  client: { patchTrip: (id: string, body: any) => Promise<any> },
  requestBody: EapiCreateTripBody,
  response: any,
  ctx: { label: string },
): Promise<boolean> {
  const tripId: string | undefined = response?.data?.id;
  if (!tripId) return false;
  const respBody = response?.data ?? response;
  const delta = diffCreateResponseForCorrection(requestBody, respBody);
  if (!delta) return false;
  console.warn(
    `[eapi stale-create] ${ctx.label} trip ${tripId} returned stale fields; issuing corrective PATCH`,
    delta,
  );
  const patchRes = await client.patchTrip(tripId, delta);
  if (patchRes?.status && patchRes.status >= 400) {
    const detail =
      typeof patchRes.data === "object"
        ? JSON.stringify(patchRes.data)
        : String(patchRes.data ?? "");
    throw new Error(
      `EAPI corrective PATCH after stale createTrip failed: ${patchRes.status} ${detail}`,
    );
  }
  return true;
}

export interface SyncPerLegTripsArgs {
  orderId: string;
  originalOrder: {
    unique_id: string;
    eapi_trip_id?: string | null;
    billing_party?: { short_code?: string | null } | null;
    trailer_id?: string | null;
    assigned_operator_id?: string | null;
    assigned_operator_2_id?: string | null;
  };
  data: any;
  legs: any[];
  /** Pre-fetched leg snapshots keyed by leg id. Helper fetches them on demand
   *  when not supplied (legacy single-leg PATCH branch). */
  legSnapshots?: Map<string, any>;
  useSameAssetsUpd: boolean;
  useSameTripTypeUpd: boolean;
  tenantId: string | null;
  /** Active tenant slug — gates bobtail EAPI eligibility (dev/staging only). */
  tenantSlug?: string | null;
  tractors: Tractor[] | undefined;
  locations: Location[] | undefined;
  drivers?: Driver[];
  resolveTrailerTypeInfo: (
    id?: string | null,
  ) => Promise<{ kind: string; length_ft: number } | null>;
  client: {
    cancelTrip: (id: string, reason?: string) => Promise<any>;
    createTrip: (body: unknown) => Promise<any>;
    patchTrip: (id: string, body: any) => Promise<any>;
    /** Optional — required when a leg's trip_type pivots to Manual so we
     *  can `/abandon` (not `/cancel`) its upstream trip. Falls back to
     *  `cancelTrip` when absent. */
    abandonTrip?: (id: string, reason?: string) => Promise<any>;
  };
  eapiEnvironment: any;
  customFields?: CustomFieldEntry[];
  pivotReasonSuffix?: string;
  enableRepair: boolean;
}

/** A created trip whose `trips` link failed; `withdrawn: false` means still live. */
export interface UnlinkedReplacementTrip {
  legSequence: number;
  eapiTripId: string;
  withdrawn: boolean;
  error: string;
}

export interface SyncPerLegTripsResult {
  pivotedLegIds: Set<string>;
  repairedLegIds: Set<string>;
  unlinkedTrips: UnlinkedReplacementTrip[];
}

/** Dispatcher-readable summary. */
export function describeUnlinkedTrips(unlinked: UnlinkedReplacementTrip[]): string {
  return unlinked
    .map((u) =>
      u.withdrawn
        ? `Leg ${u.legSequence}: new external trip ${u.eapiTripId} could not be saved (${u.error}) and was withdrawn; the leg has no external trip.`
        : `Leg ${u.legSequence}: new external trip ${u.eapiTripId} could not be saved (${u.error}) and could NOT be withdrawn — it is still live in Aurora.`,
    )
    .join(" ");
}

// `driver_status` (legs) / `status` (legacy order children) must be selected:
// the repair guard below reads it, and without it every leg looked open and a
// delivered leg could be handed a fresh external trip.
const LEG_SNAPSHOT_COLS =
  "id, leg_sequence, driver_status, ship_from_street_address, ship_from_city, ship_from_province_state, ship_from_postal_zip_code, ship_from_country, ship_to_street_address, ship_to_city, ship_to_province_state, ship_to_postal_zip_code, ship_to_country, ready_time, ready_time_end, requested_delivery_time, requested_delivery_time_end, trailer_type_id, trailer_id, assigned_tractor_id, assigned_operator_id, assigned_operator_2_id, trip_type, ship_from_location_id, ship_to_location_id, tenant_id";
/** A leg that is confirmed and not yet finished — the only legs repair may
 *  hand a missing external trip. */
const ACTIVE_LEG_STATUSES: ReadonlySet<string> = new Set([
  "confirmed",
  "tender_received",
  "in_transit",
]);
const ORDER_CHILD_SNAPSHOT_COLS = LEG_SNAPSHOT_COLS.replace(
  "driver_status",
  "status",
);


async function loadSnapshots(legIds: string[]): Promise<Map<string, any>> {
  const map = new Map<string, any>();
  if (legIds.length === 0) return map;
  const { data: legsRows } = await supabase
    .from("legs" as any)
    .select(LEG_SNAPSHOT_COLS)
    .in("id", legIds);
  (legsRows || []).forEach((r: any) => map.set(r.id, r));
  const { data: ocRows } = await supabase
    .from("orders")
    .select(ORDER_CHILD_SNAPSHOT_COLS)
    .in("id", legIds);

  (ocRows || []).forEach((r: any) => {
    if (!map.has(r.id)) map.set(r.id, r);
  });
  return map;
}

export async function syncPerLegEapiTrips(
  args: SyncPerLegTripsArgs,
): Promise<SyncPerLegTripsResult> {
  const {
    orderId,
    originalOrder,
    data,
    legs,
    useSameAssetsUpd,
    useSameTripTypeUpd,
    tenantId,
    tenantSlug,
    tractors,
    locations,
    resolveTrailerTypeInfo,
    client,
    eapiEnvironment,
    pivotReasonSuffix,
    enableRepair,
  } = args;

  const pivotedLegIds = new Set<string>();
  const repairedLegIds = new Set<string>();
  const unlinkedTrips: UnlinkedReplacementTrip[] = [];

  /** Link a new trip to its leg; on failure withdraw it and record it. */
  const linkReplacementTrip = async (
    legId: string,
    legTenantId: string,
    legSeq: number,
    newTripId: string,
  ): Promise<boolean> => {
    const res = await insertActiveLegTrip({ legId, tenantId: legTenantId, eapiTripId: newTripId });
    if (res.ok) return true;
    const error = res.error ?? "unknown error";
    let withdrawn = false;
    try {
      await withTimeout(
        Promise.resolve(client.cancelTrip(newTripId, "Untracked replacement trip")),
        undefined,
        `Withdraw untracked trip ${newTripId}`,
      );
      withdrawn = true;
    } catch (cancelErr: unknown) {
      withdrawn = isAlreadyCancelledUpstreamError(cancelErr);
      if (!withdrawn) {
        console.error(
          `[per-leg sync] leg ${legSeq} trip ${newTripId} is live upstream but unlinked:`,
          cancelErr instanceof Error ? cancelErr.message : cancelErr,
        );
      }
    }
    unlinkedTrips.push({ legSequence: legSeq, eapiTripId: newTripId, withdrawn, error });
    return false;
  };

  // Resolve snapshots (provided or fetched on demand).
  let legSnapshots = args.legSnapshots;
  if (!legSnapshots) {
    const legIds = legs.map((l) => l.id).filter(Boolean) as string[];
    legSnapshots = await loadSnapshots(legIds);
  }

  // When legs carry their own assets (!useSameAssetsUpd): build a
  // dedicated `custom_fields` set for EACH leg so the "Trailer ID" custom
  // field reflects the leg's own trailer, not just leg 1. With shared assets
  // (or a caller-supplied `customFields`), use the single shared set.
  //
  // Caching per trailerId keeps the definitions call to a single fetch
  // regardless of leg count (`buildCustomFieldsForOrder` internally caches
  // the definitions list, but composing entries is still per-call).
  const perLegFields = !useSameAssetsUpd && !args.customFields;
  const orderLevelTrailerFallback = data.trailer_id ?? originalOrder.trailer_id;
  const driversList = args.drivers;
  const orderLevelOp1Fallback = data.assigned_operator_id ?? originalOrder.assigned_operator_id ?? null;
  const orderLevelOp2Fallback = data.assigned_operator_2_id ?? originalOrder.assigned_operator_2_id ?? null;
  const emailById = (id: string | null | undefined): string | null =>
    driversList?.find((d) => d.id === (id || ""))?.email ?? null;
  const fieldCache = new Map<string, CustomFieldEntry[] | undefined>();

  const buildFieldsFor = async (
    trailerId: string | null | undefined,
    operator1Id: string | null | undefined,
    operator2Id: string | null | undefined,
    tripType?: string | null,
  ): Promise<CustomFieldEntry[] | undefined> => {
    const op1Email = emailById(operator1Id);
    const op2Email = emailById(operator2Id);
    const key = `${trailerId ?? "__none__"}|${op1Email ?? ""}|${op2Email ?? ""}|${tripType ?? ""}`;
    if (fieldCache.has(key)) return fieldCache.get(key);
    const fields = await buildCustomFieldsForOrder(client as any, eapiEnvironment, {
      customerShortCode: originalOrder.billing_party?.short_code,
      trailerId,
      operator1Email: op1Email,
      operator2Email: op2Email,
      tripType,
    });
    fieldCache.set(key, fields);
    return fields;
  };

  // Fallback for the non-per-leg branch (or when caller passed customFields).
  let sharedCustomFields = args.customFields;
  const ensureSharedCustomFields = async () => {
    if (sharedCustomFields) return sharedCustomFields;
    sharedCustomFields = await buildFieldsFor(
      orderLevelTrailerFallback,
      orderLevelOp1Fallback,
      orderLevelOp2Fallback,
      data.trip_type ?? (originalOrder as any).trip_type ?? null,
    );
    return sharedCustomFields;
  };

  const activeForPivot = await listActiveLegTripsForOrder(orderId);
  const tripByLegId = new Map(activeForPivot.map((t) => [t.leg_id, t]));


  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i];
    if (!leg.id) continue;
    const snap = legSnapshots.get(leg.id);
    const legSeq = snap?.leg_sequence ?? i + 1;
    // `legs` rows track progress in `driver_status`; legacy `orders` children
    // use `status`.
    const legProgress: string = (snap?.driver_status ?? snap?.status ?? "") as string;
    // Repair only restores a missing trip for a leg that is itself active —
    // confirmed and not yet finished (e.g. Manual → SVO, or a pivot whose
    // recreate failed). The parent's status says nothing about THIS leg on a
    // multi-leg order, and an unconfirmed leg must get its first trip from an
    // explicit Confirm, which also writes `driver_status` and runs the
    // confirm gates.
    const legIsActive = ACTIVE_LEG_STATUSES.has(legProgress);


    const effectiveTripType = useSameTripTypeUpd
      ? data.trip_type
      : (leg.trip_type || data.trip_type);
    // The order's trailer columns mirror leg 1 only. In per-leg mode a later
    // leg with no trailer type of its own is genuinely missing one — it must
    // never inherit leg 1's, however convenient that would be.
    const mirrorsParent = legMirrorsParentEquipment(legSeq, legs.length);
    const legTrailerTypeId = useSameAssetsUpd
      ? (data.trailer_type_id || (originalOrder as any).trailer_type_id)
      : (leg.trailer_type_id ||
         (mirrorsParent
           ? data.trailer_type_id || (originalOrder as any).trailer_type_id
           : null));

    const legTrailerTypeInfo = await resolveTrailerTypeInfo(legTrailerTypeId);
    const skipsEapi = isNoEapiState(
      legTrailerTypeInfo?.kind ?? null,
      effectiveTripType,
      tenantSlug,
    );

    const newTractorId = useSameAssetsUpd
      ? (data.assigned_tractor_id || "")
      : (leg.assigned_tractor_id || "");
    const origTractorId = snap?.assigned_tractor_id || "";
    const tractorChanged = newTractorId !== origTractorId;

    // Per-leg trailer change also warrants a pivot: EAPI carries the trailer
    // identity via `custom_fields`, and there is no PATCH surface for it, so a
    // cancel+recreate is the only way to refresh upstream. Order-level
    // `data.trailer_id` mirrors leg 1, so in per-leg mode it may only stand in
    // for leg 1 — a later leg without its own trailer has none.
    const newLegTrailerId = (
      useSameAssetsUpd
        ? (data.trailer_id ?? "")
        : (leg.trailer_id ?? (mirrorsParent ? data.trailer_id ?? "" : ""))
    ).toString().trim();

    const origLegTrailerId = (snap?.trailer_id ?? "").toString().trim();
    const trailerChanged = newLegTrailerId !== origLegTrailerId;

    const trip = tripByLegId.get(leg.id);

    // ── Per-leg no-EAPI switch ──
    // The leg has an active EAPI trip but its new (trailerKind, trip_type)
    // combo is no-EAPI-eligible (Manual or bobtail). Abandon (Manual) or
    // cancel (bobtail) the upstream trip and supersede the local mirror.
    // Matches the order-level `newStateSkipsEapi` branch upstream so the
    // same pivot semantics apply to per-leg trip_type toggles.
    if (trip && skipsEapi) {
      const switchedToManual = effectiveTripType === "Manual";
      const abandonMode: "abandon" | "cancel" = switchedToManual ? "abandon" : "cancel";
      const baseReason = switchedToManual
        ? "Pivoted to manual (per-leg)"
        : "Switched to Bobtail / Manual trip type (per-leg)";
      const reasonLabel = pivotReasonSuffix
        ? `${baseReason}: ${pivotReasonSuffix}`
        : baseReason;
      try {
        if (abandonMode === "abandon" && client.abandonTrip) {
          await client.abandonTrip(trip.eapi_trip_id, reasonLabel);
        } else {
          await client.cancelTrip(trip.eapi_trip_id, reasonLabel);
        }
        console.log(
          `[per-leg sync] ${abandonMode}ed leg ${legSeq} trip ${trip.eapi_trip_id} on no-EAPI switch`,
        );
      } catch (err: any) {
        if (isAlreadyCancelledUpstreamError(err)) {
          console.log(
            `[per-leg sync] leg ${legSeq} trip ${trip.eapi_trip_id} already terminal upstream on no-EAPI switch, continuing`,
          );
        } else {
          throw new Error(
            `Failed to ${abandonMode} external trip for leg ${legSeq}: ${err?.message || err}`,
          );
        }
      }
      await supersedeLegTrip(trip.trip_row_id, "no_eapi_switch");
      pivotedLegIds.add(leg.id);
      continue;
    }

    const buildLegTripBodyFromForm = async (): Promise<EapiCreateTripBody | null> => {
      const fromLoc = findLocationByAddress(
        locations,
        leg.ship_from_street_address,
        leg.ship_from_city,
      );
      const toLoc = findLocationByAddress(
        locations,
        leg.ship_to_street_address,
        leg.ship_to_city,
      );
      const readyUtc = formLocalToUtc(leg.ready_time);
      const deliveryUtc = formLocalToUtc(leg.requested_delivery_time);
      const readyEndUtc = formLocalToUtc(leg.ready_time_end || leg.ready_time);
      const deliveryEndUtc = formLocalToUtc(leg.requested_delivery_time_end || leg.requested_delivery_time);
      const legTractor = tractors?.find((t) => t.id === newTractorId) ?? null;
      // Pick the right custom-field set for THIS leg's trailer. The order's
      // trailer mirrors leg 1, so later legs never borrow it.
      const legTrailerForFields = perLegFields
        ? (leg.trailer_id ?? (mirrorsParent ? orderLevelTrailerFallback : null))
        : orderLevelTrailerFallback;

      const legOp1ForFields = perLegFields
        ? (leg.assigned_operator_id ?? orderLevelOp1Fallback)
        : orderLevelOp1Fallback;
      const legOp2ForFields = perLegFields
        ? (leg.assigned_operator_2_id ?? orderLevelOp2Fallback)
        : orderLevelOp2Fallback;
      const fields = perLegFields
        ? await buildFieldsFor(
            legTrailerForFields,
            legOp1ForFields,
            legOp2ForFields,
            effectiveTripType,
          )
        : await ensureSharedCustomFields();
      return mapLegToCreateTrip(
        {
          leg_sequence: legSeq,
          ship_from_street_address: leg.ship_from_street_address,
          ship_from_city: leg.ship_from_city,
          ship_from_province_state: leg.ship_from_province_state || null,
          ship_from_postal_zip_code: leg.ship_from_postal_zip_code || null,
          ship_from_country: leg.ship_from_country || null,
          ship_from_location_id: (leg as any).ship_from_location_id ?? null,
          ship_to_street_address: leg.ship_to_street_address,
          ship_to_city: leg.ship_to_city,
          ship_to_province_state: leg.ship_to_province_state || null,
          ship_to_postal_zip_code: leg.ship_to_postal_zip_code || null,
          ship_to_country: leg.ship_to_country || null,
          ship_to_location_id: (leg as any).ship_to_location_id ?? null,
          ready_time: readyUtc ?? "",
          ready_time_end: readyEndUtc,
          requested_delivery_time: deliveryUtc ?? "",
          requested_delivery_time_end: deliveryEndUtc,
        },
        originalOrder.unique_id,
        legTractor,
        legTrailerTypeInfo,
        locations,
        fields,
      );
    };

    // ── Pivot path: trip exists AND (tractor OR trailer) changed AND still EAPI-eligible ──
    if (trip && (tractorChanged || trailerChanged) && !skipsEapi) {
      const basePivotLabel =
        tractorChanged && trailerChanged
          ? "Truck/Trailer pivot (per-leg)"
          : trailerChanged
            ? "Trailer pivot (per-leg)"
            : "Truck pivot (per-leg)";
      const reasonLabel = pivotReasonSuffix
        ? `${basePivotLabel}: ${pivotReasonSuffix}`
        : basePivotLabel;
      const supersedeReason =
        tractorChanged && trailerChanged
          ? "truck_trailer_pivot"
          : trailerChanged
            ? "trailer_pivot"
            : "tractor_pivot";
      try {
        await client.cancelTrip(trip.eapi_trip_id, reasonLabel);
        console.log(
          `[per-leg sync] cancelled leg ${legSeq} trip ${trip.eapi_trip_id}`,
        );
      } catch (cancelErr: any) {
        if (isAlreadyCancelledUpstreamError(cancelErr)) {
          console.log(
            `[per-leg sync] leg ${legSeq} trip ${trip.eapi_trip_id} already cancelled upstream`,
          );
        } else {
          throw new Error(
            `Failed to cancel external trip for leg ${legSeq}: ${cancelErr?.message || cancelErr}`,
          );
        }
      }
      await supersedeLegTrip(trip.trip_row_id, supersedeReason);

      const tripBody = await buildLegTripBodyFromForm();
      if (tripBody) {
        try {
          assertTripBodyHasLatLng(tripBody, {
            flow: "per-leg pivot recreate",
            parentUniqueId: originalOrder.unique_id,
            legSequence: legSeq,
          });
          const res = await client.createTrip(tripBody);
          if (res?.status && res.status >= 400) {
            const detail =
              typeof res.data === "object"
                ? JSON.stringify(res.data)
                : String(res.data ?? "");
            throw new Error(`EAPI returned ${res.status} ${detail}`);
          }
          const newTripId: string | undefined = res?.data?.id;
          if (!newTripId) throw new Error("EAPI response did not include a trip ID");
          await correctStaleCreateResponse(client, tripBody, res, {
            label: `per-leg pivot recreate leg ${legSeq}`,
          });
          if (await linkReplacementTrip(leg.id, snap?.tenant_id || (tenantId as string), legSeq, newTripId)) {
            console.log(`[per-leg sync] recreated leg ${legSeq} as ${newTripId}`);
          }
        } catch (createErr: any) {
          toast.warning(
            `Leg ${legSeq}: external trip cancelled but re-creation failed (${createErr?.message || createErr}). The leg will be saved without an external trip.`,
          );
        }
      }
      pivotedLegIds.add(leg.id);
      continue;
    }

    // ── Repair path: no active trip, the leg itself is active + eligible ──

    if (enableRepair && !trip && !skipsEapi && legIsActive) {
      const tripBody = await buildLegTripBodyFromForm();
      if (!tripBody) {
        toast.warning(
          `Leg ${legSeq}: could not build the external trip request, so no external trip was created.`,
        );
        continue;
      }

      try {
        assertTripBodyHasLatLng(tripBody, {
          flow: "per-leg repair",
          parentUniqueId: originalOrder.unique_id,
          legSequence: legSeq,
        });
        const res = await client.createTrip(tripBody);
        if (res?.status && res.status >= 400) {
          const detail =
            typeof res.data === "object"
              ? JSON.stringify(res.data)
              : String(res.data ?? "");
          throw new Error(`EAPI returned ${res.status} ${detail}`);
        }
        const newTripId: string | undefined = res?.data?.id;
        if (!newTripId) throw new Error("EAPI response did not include a trip ID");
        await correctStaleCreateResponse(client, tripBody, res, {
          label: `per-leg repair leg ${legSeq}`,
        });
        if (await linkReplacementTrip(leg.id, snap?.tenant_id || (tenantId as string), legSeq, newTripId)) {
          console.log(`[per-leg sync] repaired leg ${legSeq} as ${newTripId}`);
          repairedLegIds.add(leg.id);
        }
      } catch (createErr: any) {
        toast.warning(
          `Leg ${legSeq}: failed to create missing external trip (${createErr?.message || createErr}). The leg will be saved without an external trip.`,
        );
      }
    }
  }

  return { pivotedLegIds, repairedLegIds, unlinkedTrips };
}